import { Typography } from '@euler/app/components/typography';
import { aiColors, tint } from '@euler/app/flows/ai-suite/theme';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { SSEConnection } from '@euler/lib/request/SSEClient';
import {
  ApdmCampaignSummaryResult,
  ApdmSummarySection,
  ApdmSummarySectionKind,
  apdmSummarySectionLabel,
} from '@euler/model/apdm/reporting';
import { useServiceFactory } from '@euler/services/factory';
import dayjs from 'dayjs';
import { LinearGradient } from 'expo-linear-gradient';
import { Check, RefreshCw, Sparkles } from 'lucide-react-native';
import { memo, useCallback, useEffect, useRef, useState } from 'react';
import { ActivityIndicator, Pressable, View } from 'react-native';
import { ApdmCard } from '../components/ApdmSurface';

const kMarkers: Array<[string, ApdmSummarySectionKind]> = [
  ['结果', ApdmSummarySectionKind.Outcome],
  ['效果', ApdmSummarySectionKind.Effect],
  ['预计', ApdmSummarySectionKind.Outlook],
];

const kSectionColors: Record<ApdmSummarySectionKind, string> = {
  [ApdmSummarySectionKind.Outcome]: aiColors.primary,
  [ApdmSummarySectionKind.Effect]: aiColors.violet,
  [ApdmSummarySectionKind.Outlook]: aiColors.green,
};

/** The lines written so far; a line shows once its marker has arrived. */
function sectionsSoFar(text: string): ApdmSummarySection[] {
  const sections: ApdmSummarySection[] = [];
  for (const line of text.split('\n')) {
    const match = /^\s*(\S+?)\s*[：:]\s*(.*)$/.exec(line);
    const kind = kMarkers.find(([marker]) => marker === match?.[1])?.[1];
    if (match && kind && match[2]) sections.push({ kind, text: match[2] });
  }
  return sections;
}

type Run =
  | { state: 'idle' }
  | { state: 'running'; steps: string[]; text: string }
  | { state: 'failed'; message: string }
  | { state: 'done'; summary: ApdmCampaignSummaryResult };

const Sections = memo(
  ({
    sections,
    ended,
    typing,
  }: {
    sections: ApdmSummarySection[];
    ended: boolean;
    /** Shows a caret after the line being written. */
    typing: boolean;
  }) => {
    const { rpx } = useResponsiveContext();
    return (
      <View style={{ gap: rpx(10) }}>
        {sections.map((section, index) => {
          const color = kSectionColors[section.kind];
          const caret = typing && index === sections.length - 1;
          return (
            <View key={index} style={{ flexDirection: 'row', gap: rpx(8) }}>
              <View
                style={{
                  marginTop: rpx(1),
                  paddingHorizontal: rpx(6),
                  height: rpx(20),
                  borderRadius: rpx(6),
                  justifyContent: 'center',
                  backgroundColor: tint(color, 0.12),
                }}
              >
                <Typography.Text medium size={rpx(11)} color={color}>
                  {apdmSummarySectionLabel(section.kind, ended)}
                </Typography.Text>
              </View>
              <Typography.Text
                size={rpx(13)}
                lineHeight={rpx(21)}
                color={aiColors.foreground}
                style={{ flex: 1 }}
              >
                {section.text}
                {caret ? (
                  <Typography.Text size={rpx(13)} color={aiColors.primary}>
                    ▍
                  </Typography.Text>
                ) : null}
              </Typography.Text>
            </View>
          );
        })}
      </View>
    );
  },
);

const Steps = memo(({ steps, busy }: { steps: string[]; busy: boolean }) => {
  const { rpx } = useResponsiveContext();
  return (
    <View style={{ gap: rpx(6) }}>
      {steps.map((step, index) => {
        const current = busy && index === steps.length - 1;
        return (
          <View
            key={index}
            style={{ flexDirection: 'row', alignItems: 'center', gap: rpx(6) }}
          >
            {current ? (
              <ActivityIndicator size="small" color={aiColors.primary} />
            ) : (
              <Check size={14} color={aiColors.green} />
            )}
            <Typography.Text
              size={rpx(12)}
              color={current ? aiColors.foreground : aiColors.mutedForeground}
            >
              {step}
            </Typography.Text>
          </View>
        );
      })}
    </View>
  );
});

/**
 * The AI's read of the campaign: what it produced, what made the
 * difference, and where it is heading. It is written while the reader
 * watches: each step of the work, then the lines as they come.
 */
/** Characters revealed per tick: a steady, readable typing pace. */
const kTypeStep = 2;
const kTypeTickMs = 40;

function sectionsText(sections: ApdmSummarySection[]): string {
  return sections
    .map(section => {
      const marker = kMarkers.find(([, kind]) => kind === section.kind)![0];
      return `${marker}：${section.text}`;
    })
    .join('\n');
}

/**
 * Reveals `text` at a typing pace, however fast it arrives: a model that
 * answers in a second, or a summary read back from the cache, still reads
 * as being written.
 */
function useTyped(text: string, generation: number): string {
  const [shown, setShown] = useState(0);
  useEffect(() => setShown(0), [generation]);
  useEffect(() => {
    if (shown >= text.length) return;
    const timer = setTimeout(
      () => setShown(current => Math.min(text.length, current + kTypeStep)),
      kTypeTickMs,
    );
    return () => clearTimeout(timer);
  }, [shown, text]);
  return text.slice(0, shown);
}

export const CampaignSummaryCard = memo(
  ({ campaignId, ended }: { campaignId: string; ended: boolean }) => {
    const { rpx } = useResponsiveContext();
    const { apdmService } = useServiceFactory();
    const [run, setRun] = useState<Run>({ state: 'idle' });
    // Each run types from the start, even when its text is unchanged.
    const [generation, setGeneration] = useState(0);
    const connection = useRef<SSEConnection | null>(null);

    const summarize = useCallback(() => {
      connection.current?.abort();
      setGeneration(current => current + 1);
      setRun({ state: 'running', steps: [], text: '' });
      connection.current = apdmService.streamCampaignSummary(
        { campaignId },
        {
          onNarration: text =>
            setRun(current =>
              current.state === 'running'
                ? { ...current, steps: [...current.steps, text] }
                : current,
            ),
          onToken: text =>
            setRun(current =>
              current.state === 'running'
                ? { ...current, text: current.text + text }
                : current,
            ),
          onDone: summary => setRun({ state: 'done', summary }),
          onError: message => {
            console.error('APDM campaign summary failed', message);
            setRun({ state: 'failed', message });
          },
        },
      );
    }, [apdmService, campaignId]);

    useEffect(() => {
      setRun({ state: 'idle' });
      // An ended campaign's summary was written when it ended; show it.
      if (ended) summarize();
      return () => connection.current?.abort();
    }, [ended, summarize]);

    // The saved lines replace the streamed ones once done; they are the
    // same text, checked, so typing carries on where it was.
    const text =
      run.state === 'running'
        ? run.text
        : run.state === 'done'
        ? sectionsText(run.summary.sections)
        : '';
    const typed = useTyped(text, generation);
    const sections = sectionsSoFar(typed);
    const typing = run.state === 'running' || typed.length < text.length;
    return (
      <ApdmCard
        style={{ overflow: 'hidden', borderColor: tint(aiColors.violet, 0.25) }}
      >
        {/* The AI's own surface: the chat's blue-to-violet, kept light. */}
        <LinearGradient
          colors={[tint(aiColors.primary, 0.1), tint(aiColors.violet, 0.16)]}
          start={{ x: 0, y: 0 }}
          end={{ x: 1, y: 1 }}
          style={{ padding: rpx(16), gap: rpx(12) }}
        >
          <View
            style={{ flexDirection: 'row', alignItems: 'center', gap: rpx(6) }}
          >
            <Sparkles size={16} color={aiColors.primary} />
            <Typography.Text
              medium
              size={rpx(14)}
              color={aiColors.foreground}
              style={{ flex: 1 }}
            >
              AI 总结
            </Typography.Text>
            {run.state === 'done' && !typing ? (
              <>
                <Typography.Text
                  size={rpx(11)}
                  color={aiColors.mutedForeground}
                >
                  {dayjs(run.summary.generatedAt).format('M月D日 HH:mm')} 生成
                </Typography.Text>
                <Pressable
                  accessibilityRole="button"
                  accessibilityLabel="重新总结"
                  hitSlop={8}
                  onPress={summarize}
                >
                  <RefreshCw size={14} color={aiColors.mutedForeground} />
                </Pressable>
              </>
            ) : null}
          </View>
          {sections.length ? (
            <Sections sections={sections} ended={ended} typing={typing} />
          ) : run.state === 'running' || typing ? (
            // The steps give way to the summary once its first line arrives.
            <Steps
              steps={
                run.state === 'running' && run.steps.length
                  ? run.steps
                  : ['正在读取这次活动的发送和回复']
              }
              busy
            />
          ) : (
            <>
              <Typography.Text size={rpx(12)} color={aiColors.mutedForeground}>
                {run.state === 'failed'
                  ? run.message
                  : '看看这次活动带来了什么、哪条内容效果好，以及到结束时大概的效果。会用掉少量 AI 积分。'}
              </Typography.Text>
              <Pressable
                accessibilityRole="button"
                onPress={summarize}
                style={{
                  alignSelf: 'flex-start',
                  paddingHorizontal: rpx(14),
                  paddingVertical: rpx(8),
                  borderRadius: rpx(10),
                  backgroundColor: aiColors.primary,
                }}
              >
                <Typography.Text medium size={rpx(12)} color="#fff">
                  {run.state === 'failed' ? '再试一次' : '生成总结'}
                </Typography.Text>
              </Pressable>
            </>
          )}
        </LinearGradient>
      </ApdmCard>
    );
  },
);
